import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Search, MapPin, Calendar, Users, ChevronRight, Star, 
  Shield, Clock, HeartHandshake, Award, Play, ArrowRight,
  Plane, Heart, Users2, Mountain, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export default function HomePage() {
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [searchData, setSearchData] = useState({
    destination: '',
    date: '',
    travelers: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [destRes, packRes, vidRes] = await Promise.all([
        axios.get(`${API}/destinations`),
        axios.get(`${API}/packages`),
        axios.get(`${API}/videos`)
      ]);
      setDestinations(Array.isArray(destRes.data) ? destRes.data : []);
setPackages(Array.isArray(packRes.data) ? packRes.data : []);
setVideos(Array.isArray(vidRes.data) ? vidRes.data : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const specialDeals = [
    { icon: Heart, title: 'Honeymoon Packages', desc: 'Romantic getaways', color: 'bg-rose-100 text-rose-600' },
    { icon: Users2, title: 'Family Holidays', desc: 'Fun for everyone', color: 'bg-blue-100 text-blue-600' },
    { icon: Mountain, title: 'Adventure Tours', desc: 'Thrilling experiences', color: 'bg-amber-100 text-amber-600' },
    { icon: Users, title: 'Group Tours', desc: 'Travel with friends', color: 'bg-purple-100 text-purple-600' },
    { icon: Sparkles, title: 'Weekend Trips', desc: 'Quick escapes', color: 'bg-teal-100 text-teal-600' },
  ];

  const whyChooseUs = [
    { icon: Shield, title: 'Best Price Guarantee', desc: 'We match any competitor price' },
    { icon: Award, title: 'Expert Travel Planners', desc: '10+ years of experience' },
    { icon: HeartHandshake, title: 'Customized Packages', desc: 'Tailored to your needs' },
    { icon: Clock, title: '24/7 Support', desc: 'Always here for you' },
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80"
            alt="Travel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-medium text-white tracking-tight leading-none mb-6">
              Explore the World with{' '}
              <span className="text-teal-400">ID4U Travels</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 mb-10 leading-relaxed max-w-xl">
              Curated tours for the modern traveler. Discover destinations that inspire and create memories that last forever.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/packages" data-testid="hero-explore-btn">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 py-6 text-lg font-medium">
                  Explore Tour Packages
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/flights" data-testid="hero-flights-btn">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm">
                  <Plane className="mr-2 w-5 h-5" />
                  Book Flights
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Search Widget */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 md:mt-24"
          >
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.2)] p-6 md:p-8 max-w-4xl">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Destination</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Where to?"
                      value={searchData.destination}
                      onChange={(e) => setSearchData({ ...searchData, destination: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                      data-testid="search-destination"
                    />
                  </div>
                </div>
                <div className="relative">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Travel Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="date"
                      value={searchData.date}
                      onChange={(e) => setSearchData({ ...searchData, date: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                      data-testid="search-date"
                    />
                  </div>
                </div>
                <div className="relative">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Travelers</label>
                  <div className="relative">
                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      value={searchData.travelers}
                      onChange={(e) => setSearchData({ ...searchData, travelers: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all appearance-none bg-white"
                      data-testid="search-travelers"
                    >
                      <option value="">Select</option>
                      <option value="1">1 Person</option>
                      <option value="2">2 People</option>
                      <option value="3">3 People</option>
                      <option value="4">4+ People</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-end">
                  <Link to={`/packages?dest=${searchData.destination}`} className="w-full" data-testid="search-btn">
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 h-[50px] font-medium">
                      <Search className="mr-2 w-5 h-5" />
                      Search
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-white to-slate-50" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-teal-600 font-semibold tracking-wider uppercase text-sm">Explore</span>
            <h2 className="text-4xl md:text-5xl font-medium text-slate-900 mt-3 mb-4">Popular Destinations</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Discover breathtaking locations handpicked by our travel experts
            </p>
          </motion.div>

          {/* Bento Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
              {Array.isArray(destinations) &&
            destinations.slice(0, 9).map((dest, index) => (
              <motion.div
                key={dest.id}
                variants={fadeInUp}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer ${
                  index === 0 ? 'col-span-2 row-span-2' : 
                  index === 3 ? 'col-span-2' : ''
                }`}
                data-testid={`destination-${dest.id}`}
              >
                <Link to={`/packages?dest=${dest.id}`}>
                  <div className={`relative ${index === 0 ? 'h-[400px] md:h-[500px]' : 'h-[200px] md:h-[240px]'}`}>
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                      <h3 className={`text-white font-semibold mb-1 ${index === 0 ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'}`}>
                        {dest.name}
                      </h3>
                      <p className="text-white/80 text-sm mb-2">{dest.description}</p>
                      <div className="flex items-center gap-2 text-teal-300">
                        <span className="text-sm font-medium">From ₹{dest.packages_from?.toLocaleString()}</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="packages-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-12"
          >
            <div>
              <span className="text-teal-600 font-semibold tracking-wider uppercase text-sm">Featured</span>
              <h2 className="text-4xl md:text-5xl font-medium text-slate-900 mt-3 mb-2">Tour Packages</h2>
              <p className="text-lg text-slate-500">Handcrafted itineraries for unforgettable experiences</p>
            </div>
            <Link to="/packages" className="mt-6 md:mt-0" data-testid="view-all-packages">
              <Button variant="outline" className="rounded-full">
                View All Packages
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {Array.isArray(packages) &&
  packages.slice(0, 6).map((pkg) => (
              <motion.div
                key={pkg.id}
                variants={fadeInUp}
                className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.12)] transition-all duration-300"
                data-testid={`package-${pkg.id}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  {pkg.badge && (
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                      {pkg.badge}
                    </span>
                  )}
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-slate-700">
                    {pkg.duration}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-1">{pkg.name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {pkg.destination}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {pkg.highlights?.slice(0, 2).map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-100 rounded-md text-xs text-slate-600">
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <span className="text-sm text-slate-400 line-through">₹{pkg.original_price?.toLocaleString()}</span>
                      <p className="text-2xl font-bold text-teal-700">₹{pkg.price?.toLocaleString()}</p>
                    </div>
                    <Link to={`/packages/${pkg.id}`} data-testid={`view-pkg-${pkg.id}`}>
                      <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-teal-700 to-teal-900" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-teal-300 font-semibold tracking-wider uppercase text-sm">Why ID4U</span>
            <h2 className="text-4xl md:text-5xl font-medium text-white mt-3 mb-4">Why Choose Us</h2>
            <p className="text-lg text-teal-100 max-w-2xl mx-auto">
              Trusted by thousands of travelers for exceptional service
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-colors duration-300"
                data-testid={`why-us-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-teal-100">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-center">
              <div>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">10K+</p>
                <p className="text-teal-200">Happy Travelers</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">500+</p>
                <p className="text-teal-200">Tour Packages</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">50+</p>
                <p className="text-teal-200">Destinations</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">4.9</p>
                <p className="text-teal-200">Customer Rating</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Special Deals */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="deals-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-teal-600 font-semibold tracking-wider uppercase text-sm">Special Offers</span>
            <h2 className="text-4xl md:text-5xl font-medium text-slate-900 mt-3 mb-4">Travel Categories</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6"
          >
            {specialDeals.map((deal, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group bg-slate-50 hover:bg-white rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:shadow-lg"
                data-testid={`deal-${index}`}
              >
                <div className={`w-16 h-16 rounded-2xl ${deal.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <deal.icon className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{deal.title}</h3>
                <p className="text-sm text-slate-500">{deal.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Video Reels */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-900" data-testid="videos-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-12"
          >
            <div>
              <span className="text-teal-400 font-semibold tracking-wider uppercase text-sm">Watch</span>
              <h2 className="text-4xl md:text-5xl font-medium text-white mt-3 mb-2">Travel Reels</h2>
              <p className="text-lg text-slate-400">Get inspired by stunning destinations around the world</p>
            </div>
            <Link to="/videos" className="mt-6 md:mt-0" data-testid="view-all-videos">
              <Button variant="outline" className="rounded-full border-slate-600 text-slate-300 hover:bg-slate-800">
                View All Videos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" data-testid="video-slider">
              {Array.isArray(videos) &&
  videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex-none w-48 md:w-56 snap-start"
                  data-testid={`video-${video.id}`}
                >
                  <a
                    href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-slate-800">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-6 h-6 text-white fill-white" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white font-medium text-sm">{video.title}</p>
                      </div>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-orange-100 via-white to-teal-50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6">
              Ready for Your Next Adventure?
            </h2>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              Let us help you plan the perfect trip. Our travel experts are ready to create a customized itinerary just for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trip-planner" data-testid="cta-plan-trip">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 py-6 text-lg font-medium">
                  Plan Your Trip
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="cta-contact">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
